'use client'

import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faStar, faX, faTag } from '@fortawesome/free-solid-svg-icons'
import Link from 'next/link'
import { sendGTMEvent } from '@next/third-parties/google'
import { useRouter } from 'next/navigation'
import { getPrices } from '@/lib/features/prices/pricesSlice'
import { useAppSelector } from '@/lib/store'
import { getCurrentUser, getCurrentUserIsLoggedIn } from '@/lib/features/currentUser/currentUserSlice'
import { MEMBERSHIP_TYPE_NAME } from '@/lib/constants'
import { EnumMembershipPeriod, EnumMembershipType } from '../../../../../../../../lib/database/lib/generated/enums'
import { getToken } from '@/lib/features/token/tokenSlice'
import { getFirstActivePublicCoupon } from '@/lib/features/coupons/couponsSlice'
import { getDiscount } from '@/lib/utils'
import { clientApiService } from '@/services/client-api-service'
import type { Price } from '@/types/entities'

interface Props {
  price?: Price
  membershipPeriod: string
  gtmItemListId: string
  gtmItemListName: string
}

export default (({ price, membershipPeriod, gtmItemListId, gtmItemListName }): React.ReactElement => {
  const router = useRouter()
  const token = useAppSelector(getToken)
  const user = useAppSelector(getCurrentUser)
  const prices = useAppSelector(getPrices)
  const isLoggedIn = useAppSelector(getCurrentUserIsLoggedIn)
  const coupon = useAppSelector(getFirstActivePublicCoupon)

  const [isPreparingSession, setIsPreparingSession] = React.useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)

  const isAnnual = membershipPeriod === EnumMembershipPeriod.YEARLY

  let liteMonthlyDiscount
  let proMonthlyDiscount
  let proYearlyDiscount
  let premiumMontlyDiscount
  let premiumYearlyDiscount

  const liteMonthly = prices?.find(
    (getPrice) => getPrice?.membership_type === EnumMembershipType.LITE && getPrice?.membership_period === EnumMembershipPeriod.MONTHLY,
  )

  const liteYearly = prices?.find(
    (getPrice) => getPrice?.membership_type === EnumMembershipType.LITE && getPrice?.membership_period === EnumMembershipPeriod.YEARLY,
  )

  const proMonthly = prices?.find(
    (getPrice) => getPrice?.membership_type === EnumMembershipType.PRO && getPrice?.membership_period === EnumMembershipPeriod.MONTHLY,
  )

  const proYearly = prices?.find(
    (getPrice) => getPrice?.membership_type === EnumMembershipType.PRO && getPrice?.membership_period === EnumMembershipPeriod.YEARLY,
  )

  const premiumMonthly = prices?.find(
    (getPrice) => getPrice?.membership_type === EnumMembershipType.PREMIUM && getPrice?.membership_period === EnumMembershipPeriod.MONTHLY,
  )

  const premiumYearly = prices?.find(
    (getPrice) => getPrice?.membership_type === EnumMembershipType.PREMIUM && getPrice?.membership_period === EnumMembershipPeriod.YEARLY,
  )

  const liteYearlyPrice = liteYearly ? +liteYearly.price / 100 ?? 0 : 0
  const liteMonthlyPrice = liteMonthly ? +liteMonthly.price / 100 ?? 0 : 0
  const proMonthlyPrice = proMonthly ? +proMonthly.price / 100 ?? 0 : 0
  const proYearlyPrice = proYearly ? +proYearly.price / 100 ?? 0 : 0
  const premiumMonthlyPrice = premiumMonthly ? +premiumMonthly.price / 100 ?? 0 : 0
  const premiumYearlyPrice = premiumYearly ? +premiumYearly.price / 100 ?? 0 : 0
  const liteMontlyCredit = liteMonthly?.num_credits ?? 0
  const ProMonthlyCredit = proMonthly?.num_credits ?? 0
  const PremiumMonthlyCredit = premiumMonthly?.num_credits ?? 0

  if (liteMonthlyPrice !== 0) {
    const result = (liteYearlyPrice / (liteMonthlyPrice * 12)) * 100

    liteMonthlyDiscount = (100 - result).toFixed(0)
  }

  if (proMonthlyPrice !== 0) {
    const result = (proMonthlyPrice / (liteMonthlyPrice * (ProMonthlyCredit / liteMontlyCredit))) * 100

    proMonthlyDiscount = (100 - result).toFixed(0)
  }

  if (proYearlyPrice !== 0) {
    const result = (proYearlyPrice / (liteMonthlyPrice * 12 * (ProMonthlyCredit / liteMontlyCredit))) * 100

    proYearlyDiscount = (100 - result).toFixed(0)
  }

  if (premiumMonthlyPrice !== 0) {
    const result = (premiumMonthlyPrice / (liteMonthlyPrice * 2 * (PremiumMonthlyCredit / ProMonthlyCredit))) * 100

    premiumMontlyDiscount = (100 - result).toFixed(0)
  }

  if (premiumYearlyPrice !== 0) {
    const result = (premiumYearlyPrice / (liteMonthlyPrice * 12 * (PremiumMonthlyCredit / liteMontlyCredit))) * 100

    premiumYearlyDiscount = (100 - result).toFixed(0)
  }

  let membershipPeriodLabel: string

  if (!price) {
    membershipPeriodLabel = membershipPeriod === EnumMembershipPeriod.MONTHLY ? 'month' : 'year'
  } else {
    const period = price?.membership_period

    membershipPeriodLabel = period === EnumMembershipPeriod.MONTHLY ? 'month' : 'year'
  }

  let isCurrentPlan = false
  let isCurrentPlanStyle = ''
  let buttonColor = 'bg-primary hover:bg-secondary'
  let unclickableButtonColor = 'bg-primary'
  let buttonText = isLoggedIn ? 'Choose this plan' : 'Get Started'

  if (
    user?.user_membership?.membership_type === price?.membership_type &&
    user?.user_membership?.membership_period === price?.membership_period &&
    !user?.user_membership?.canceled_at
  ) {
    isCurrentPlan = true

    if (price) {
      isCurrentPlanStyle = 'border-2 border-primary'
      buttonText = 'Current plan (Renew)'
    } else {
      isCurrentPlanStyle = 'opacity-60 border-2 border-primary'
      buttonText = 'Current plan'
    }
  } else if (!price) {
    buttonColor = 'bg-red-600 hover:bg-red-400'
    unclickableButtonColor = 'bg-red-600'
    buttonText = 'Unsubscribe'
  }

  const title = price?.membership_type ? MEMBERSHIP_TYPE_NAME[price.membership_type as keyof typeof MEMBERSHIP_TYPE_NAME] : 'Free'

  let subtitle = null
  let bulletPoints = null
  let bestValueTag = false
  let displaySavingPrice = null

  const handleSessionStart = (): void => {
    if (!isLoggedIn) {
      setIsModalOpen(true)

      return
    }

    if (!price) {
      router.push('/account/subscription')

      return
    }

    void (async () => {
      setIsPreparingSession(true)

      try {
        const { body, status } = await clientApiService.getStripeSession.query({
          params: {
            priceId: price.id,
          },
          extraHeaders: {
            authorization: token ? `Bearer ${token}` : undefined,
          },
        })

        if (status === 200) {
          if (process.env['NEXT_PUBLIC_SVGIO_STAGE'] === 'prod') {
            const gtmItems = [
              {
                item_id: price.stripe_prod_price_id,
                item_name: `${price.membership_type?.charAt(0).toUpperCase()}${price.membership_type?.slice(1).toLowerCase()} Membership`,
                coupon: coupon?.promotion_code,
                discount: getDiscount(price, coupon),
                index: 0,
                item_variant: `${price.membership_period?.charAt(0).toUpperCase()}${price.membership_period?.slice(1).toLowerCase()}`,
                price: Number((price.price / 100).toFixed(2)),
                promotion_id: coupon?.stripe_promo_id,
                promotion_name: coupon?.name,
                quantity: 1,
              },
            ]

            sendGTMEvent({ ecommerce: null })
            sendGTMEvent({
              event: 'select_item',
              user_id: user?.id,
              ecommerce: {
                item_list_id: gtmItemListId,
                item_list_name: gtmItemListName,
                items: gtmItems,
              },
            })

            sendGTMEvent({ ecommerce: null })
            sendGTMEvent({
              event: 'begin_checkout',
              user_id: user?.id,
              ecommerce: {
                value: price ? Number((price.price / 100).toFixed(2)) : undefined,
                currency: price?.currency.toUpperCase(),
                items: gtmItems,
              },
            })
          }

          window.location.href = body.data.checkoutUrl

          return
        }

        setIsModalOpen(true)
      } catch (error: unknown) {
        setIsModalOpen(true)
      }

      setIsPreparingSession(false)
    })()
  }

  if (!price) {
    subtitle = 'Best option to test our tool.'
    bulletPoints = (
      <>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>5 images per day</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Latest 5 images history</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faX} className="size-3.5 text-red-600" />
          <span>Image + Text Prompt</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Commercial license</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>No watermark</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>No ads</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faX} className="size-3.5 text-red-600" />
          <span>Images are private</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faX} className="size-3.5 text-red-600" />
          <span>Access to all styles</span>
        </li>
      </>
    )
  } else if (price.membership_type === EnumMembershipType.LITE) {
    subtitle = 'Best option for small projects.'
    displaySavingPrice = isAnnual ? `You save ${liteMonthlyDiscount} %` : null
    bulletPoints = (
      <>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">500</b> images per month
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">Latest 250</b> images history
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">Image + Text Prompt</b>
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Commercial license</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>No watermark</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>No ads</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Images are private</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Access to all styles</span>
        </li>
      </>
    )
  } else if (price.membership_type === EnumMembershipType.PRO) {
    bestValueTag = true
    subtitle = 'Best option for medium projects.'
    displaySavingPrice = isAnnual ? `You save ${proYearlyDiscount} %` : `You save ${proMonthlyDiscount} %`
    bulletPoints = (
      <>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">1000</b> images per month
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">Latest 500</b> images history
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">Image + Text Prompt</b>
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Commercial license</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>No watermark</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>No ads</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Images are private</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Access to all styles</span>
        </li>
      </>
    )
  } else if (price.membership_type === EnumMembershipType.PREMIUM) {
    subtitle = 'Best option for large projects.'
    displaySavingPrice = isAnnual ? `You save ${premiumYearlyDiscount} %` : `You save ${premiumMontlyDiscount} %`
    bulletPoints = (
      <>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">2000</b> images per month
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">Unlimited</b> images history
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>
            <b className="text-semibold">Image + Text Prompt</b>
          </span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Commercial license</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>No watermark</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>No ads</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Images are private</span>
        </li>
        <li className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faCheck} className="size-4 text-emerald-400" />
          <span>Access to all styles</span>
        </li>
      </>
    )
  }

  return (
    <>
      <div className={`border-light relative flex max-w-lg flex-col rounded-lg border-2 bg-white p-6 shadow xl:p-8 ${isCurrentPlanStyle}`}>
        {price && displaySavingPrice && (
          <span className="bg-light text-primary absolute right-3 top-3 flex items-center rounded-lg px-3 py-1 text-sm font-semibold">
            <FontAwesomeIcon icon={faTag} className="text-primary mr-1.5 size-3" width={8} height={8} />
            {displaySavingPrice}
          </span>
        )}
        <h3 className="text-primary mb-4 flex items-center justify-between text-xl font-semibold xl:text-2xl">
          {bestValueTag ? (
            <div className="flex">
              <span>{title}</span>
              <div className="ml-2 flex items-center">
                <FontAwesomeIcon icon={faStar} className="text-primary size-5" width={24} height={24} />
              </div>
            </div>
          ) : (
            <span>{title}</span>
          )}
        </h3>

        <p className="text-baseSM font-light">{subtitle}</p>
        <div className="my-8 flex items-baseline">
          <span className="mr-2 text-4xl font-semibold lg:text-5xl xl:text-5xl">${price?.price ? Math.floor(price.price / 100) : '0'}</span>
          <span className="font-light">/{membershipPeriodLabel}</span>
        </div>
        <ul className="text-baseSM mb-8 space-y-4 text-left">{bulletPoints}</ul>
        {isPreparingSession || (!price && isCurrentPlan) ? (
          <p className={`${unclickableButtonColor} rounded-md p-4 text-center text-white ${isPreparingSession ? 'opacity-60' : ''}`}>
            {isPreparingSession && (
              <svg
                aria-hidden="true"
                role="status"
                className="me-3 inline size-5 animate-spin text-white"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {buttonText}
          </p>
        ) : (
          <button
            type="button"
            onClick={handleSessionStart}
            className={`${buttonColor} rounded-md p-4 text-center text-white transition-all duration-300 ease-in-out`}
          >
            {buttonText}
          </button>
        )}
      </div>
      {isModalOpen && (
        <div className="fixed left-0 top-0 z-10 flex size-full items-center justify-center bg-white/90 backdrop-blur-sm">
          <div className="relative max-w-sm rounded-lg bg-white shadow-md xl:max-w-lg">
            <div className="relative px-8 py-6">
              <h3 className="font-bold uppercase">Must log in for checkout</h3>
              <button
                type="button"
                aria-label="Close"
                className="absolute right-4 top-1/2 -translate-y-1/2"
                onClick={() => {
                  setIsModalOpen(false)
                }}
              >
                <FontAwesomeIcon icon={faX} className="text-default size-4" />
              </button>
            </div>
            <div className="p-8 pt-2">
              <p className="mb-6">You must be logged in before checkout so that we can match your membership to your account.</p>
              <Link
                href="/login"
                className="bg-primary hover:bg-secondary inline-block rounded px-6 py-2 text-white transition-all duration-300 ease-in-out"
              >
                Log in
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}) satisfies React.FC<Props>
